import React from 'react';
import { Card, Row, Col, Statistic, Progress, Spin, Empty, Tooltip } from 'antd';
import { ArrowUpOutlined, ArrowDownOutlined, ClockCircleOutlined, CheckCircleOutlined } from '@ant-design/icons';
import { useTranslationWithNamespace } from '../../hooks/useTranslationWithNamespace';
import { useDepositDetectionMetrics } from '../../hooks/useIndexerManagement';

const DepositMetricsPanel: React.FC = () => {
  const { t: indexerT } = useTranslationWithNamespace('indexer');
  const { data: metrics, isLoading, isError } = useDepositDetectionMetrics();

  if (isLoading) {
    return (
      <Card title={indexerT('depositMetrics.title')}>
        <div style={{ textAlign: 'center', padding: '20px' }}>
          <Spin size="large" />
        </div>
      </Card>
    );
  }

  if (isError || !metrics) {
    return (
      <Card title={indexerT('depositMetrics.title')}>
        <Empty description={indexerT('depositMetrics.noData')} />
      </Card>
    );
  }

  // Calculate percentage change for 24h deposits
  const percentChange = (metrics?.depositsLast24h || 0) > 0 && (metrics?.totalDeposits || 0) > 0
    ? (((metrics?.depositsLast24h || 0) / ((metrics?.totalDeposits || 0) - (metrics?.depositsLast24h || 0))) * 100).toFixed(2)
    : '0.00';

  // Format time in minutes
  const formatTime = (timeInMs: number) => {
    const minutes = Math.floor(timeInMs / 60000);
    const seconds = Math.floor((timeInMs % 60000) / 1000);
    return `${minutes}m ${seconds}s`;
  };

  return (
    <Card title={indexerT('depositMetrics.title')}>
      <Row gutter={[16, 16]}>
        <Col xs={24} sm={12} md={6}>
          <Statistic
            title={indexerT('depositMetrics.totalDeposits')}
            value={metrics?.totalDeposits || 0}
            prefix={<CheckCircleOutlined />}
          />
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Statistic
            title={indexerT('depositMetrics.totalAmount')}
            value={metrics?.totalAmount || 0}
            precision={2}
          />
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Tooltip title={indexerT('depositMetrics.last24hTooltip')}>
            <Statistic
              title={indexerT('depositMetrics.depositsLast24h')}
              value={metrics?.depositsLast24h || 0}
              valueStyle={{ color: (metrics?.depositsLast24h || 0) > 0 ? '#3f8600' : '#cf1322' }}
              prefix={(metrics?.depositsLast24h || 0) > 0 ? <ArrowUpOutlined /> : <ArrowDownOutlined />}
              suffix={`${percentChange}%`}
            />
          </Tooltip>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Statistic
            title={indexerT('depositMetrics.amountLast24h')}
            value={metrics?.amountLast24h || 0}
            precision={2}
          />
        </Col>
      </Row>

      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
        <Col xs={24} sm={12} md={6}>
          <Statistic
            title={indexerT('depositMetrics.depositsLast7d')}
            value={metrics?.depositsLast7d || 0}
          />
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Statistic
            title={indexerT('depositMetrics.amountLast7d')}
            value={metrics?.amountLast7d || 0}
            precision={2}
          />
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Tooltip title={indexerT('depositMetrics.avgConfirmationTimeTooltip')}>
            <Statistic
              title={indexerT('depositMetrics.avgConfirmationTime')}
              value={formatTime(metrics?.avgConfirmationTime || 0)}
              prefix={<ClockCircleOutlined />}
            />
          </Tooltip>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Tooltip title={indexerT('depositMetrics.avgProcessingTimeTooltip')}>
            <Statistic
              title={indexerT('depositMetrics.avgProcessingTime')}
              value={formatTime(metrics?.avgProcessingTime || 0)}
              prefix={<ClockCircleOutlined />}
            />
          </Tooltip>
        </Col>
      </Row>

      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
        <Col span={24}>
          <h4>{indexerT('depositMetrics.byBlockchain')}</h4>
          {metrics?.byBlockchain && Array.isArray(metrics.byBlockchain) ? (
            metrics.byBlockchain.map((item: any) => {
              // Handle both field names (blockchain and name)
              const blockchain = item.blockchain || item.name || 'unknown';
              const deposits = item.deposits || item.count || 0;

              return (
                <div key={blockchain} style={{ marginBottom: 8 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span>{blockchain.toUpperCase()}</span>
                    <span>{deposits} deposits ({item.amount})</span>
                  </div>
                  <Progress
                    percent={Math.round((deposits / (metrics?.totalDeposits || 1)) * 100)}
                    showInfo={false}
                    status="active"
                    strokeColor={{
                      '0%': '#108ee9',
                      '100%': '#87d068',
                    }}
                  />
                </div>
              );
            })
          ) : (
            <Empty description={indexerT('depositMetrics.noBlockchainData', 'No blockchain data available')} />
          )}
        </Col>
      </Row>
    </Card>
  );
};

export default DepositMetricsPanel;
